import { Outlet, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { identityApi } from '../shared/api/identity';
import { applyTheme, getStoredTheme, storeTheme } from '../shared/hooks/useTheme';
import { OfflineBanner } from '../shared/components/OfflineBanner';

export function Shell() {
  // Mobile off-canvas nav drawer state. On md+ the sidebar is static and this
  // is ignored. Close it on every route change so navigating from the drawer
  // dismisses it.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  // When the user logs in, sync their server-side theme preference to this
  // device. Local override (set via Settings) takes precedence on subsequent
  // boots - we only fall back to server-side when no local choice exists.
  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => identityApi.me(),
    staleTime: 60_000,
  });
  useEffect(() => {
    if (!me.data?.theme) return;
    const stored = getStoredTheme();
    // Only adopt the server theme if the user hasn't picked one locally.
    if (typeof window !== 'undefined' && !window.localStorage.getItem('tc-crm-theme')) {
      storeTheme(me.data.theme);
      applyTheme(me.data.theme);
    } else if (stored !== me.data.theme) {
      // Local choice wins - but apply it anyway in case applyTheme on App
      // mount missed (rare race during first paint).
      applyTheme(stored);
    }
  }, [me.data?.theme]);

  return (
    <div className="relative flex h-full w-full">
      <OfflineBanner />
      <Sidebar mobileOpen={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />
      <div className="flex h-full min-w-0 flex-1 flex-col">
        <Topbar onMenuClick={() => setMobileNavOpen(true)} />
        <main className="flex-1 overflow-auto">
          <div className="animate-fade-in min-h-full">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
